import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Typography,
    TextField,
    InputAdornment,
    IconButton,
    Dialog,
    DialogContent,
    Autocomplete,
    Button,
    CircularProgress,
    Alert,
    Skeleton,
    TableSortLabel,
    Tooltip,
} from '@mui/material';
import { Search, Inventory2, ArrowForward, Close, LocalShipping, Refresh, WarningAmber, AutoAwesome, Edit, PlaylistAddCheck, CloudSync, Download } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { WarehouseAPI } from '../services/api';
import { CardingShelfRow, CardingShelfDataPage } from '../types';
import { ImageThumb, ImageLightbox, LightboxSection } from '../components/carding/CardingImageComponents';
import EditPackagingDialog from '../components/carding/EditPackagingDialog';
import BulkPackagingImportDialog from '../components/carding/BulkPackagingImportDialog';
import FilterDropdown from '../components/common/FilterDropdown';
import { useToast } from '../components/common/useToast';
import TablePaginationBar, { DEFAULT_PAGE_SIZE } from '../components/common/TablePaginationBar';
import { useAuth } from '../contexts/AuthContext';
import { usePermission } from '../hooks/usePermission';

const MotionTableRow = motion(TableRow);

// Fallback only — used if the live /api/carding/shelf-options fetch fails. The real
// list is pulled live from ShelfwiseInventory (synced from Unicommerce) at runtime.
const FALLBACK_SHELF_OPTIONS = [
    'CARDING', 'CARDING-CONSUMPTION', 'B2B-BLINKIT', 'B2B-CONSUMPTION', 'B2B-FBA',
    'B2B-KLYDO', 'B2B-KNOT', 'B2B-MYNTRA', 'B2B-SLIKK', 'B2B-ZEPTO', 'B2B-ZILO',
    'BLINKIT', 'BLINKIT-NC', 'D2C-CONSUMPTION', 'D2C-GB', 'DEFAULT',
    'GF001-MIX_RTO', 'GF002-MIX_RTO', 'GF003-MIX_RTO', 'GF004-MIX_RTO', 'GF005-MIX_RTO',
    'NECKLACE-CARD', 'NON-CARDED', 'PACKAGING',
].sort();

// Suggests a packaging-consumption destination shelf based on the SKU's target
// shelf/channel — user can always override the suggestion.
const suggestPackagingShelf = (targetShelf: string): string => {
    if (/^B2B-/i.test(targetShelf) || /^BLINKIT/i.test(targetShelf)) return 'B2B-CONSUMPTION';
    return 'CARDING-CONSUMPTION';
};

// The Unicommerce inventory-adjust webhook wraps its response in an array (one entry
// per top-level call) — each entry then has its own inventoryAdjustmentResponses array
// (one per adjustment in that call's payload). Flatten across both levels so a single
// batch that contains multiple items/legs is checked correctly either way.
const extractAdjustmentResponses = (data: any): any[] => {
    const entries = Array.isArray(data) ? data : [data];
    return entries.flatMap(e => (Array.isArray(e?.inventoryAdjustmentResponses) ? e.inventoryAdjustmentResponses : []));
};

const PLYTIX_SYNC_BATCH_SIZE = 100;
// Server-side calls to the inventory-adjust webhook are IP-blocked, so the Unicommerce
// call is made from the user's browser via WarehouseAPI.postInventoryAdjust (which
// first claims an idempotency key from the backend so a move is sent at most once).

type SortKey = 'skuCode' | 'shelfCode' | 'quantity';

const CardingTab: React.FC = () => {
    const { user } = useAuth();
    // Mirrors backend requireAdmin (admin OR warehouse-manager) — gates the catalog re-sync.
    const canSyncCatalog = !!user?.isAdmin || user?.role === 'admin' || user?.role === 'warehouse-manager';
    // RBAC: carding mutations (move, damage, packaging-mapping edits/imports) are gated on
    // the carding matrix capability. A view-only caller sees them hidden/disabled; the
    // backend independently rejects any write with 403 (defense in depth, SI-5).
    const canEdit = usePermission('carding').can('edit');

    const queryClient = useQueryClient();
    const [syncingPlytix, setSyncingPlytix] = useState(false);

    // Packaging-mapping UI state (FEAT-003): per-SKU edit dialog, bulk-import dialog, and
    // the admin-gated catalog re-sync.
    const [editPkgRow, setEditPkgRow] = useState<CardingShelfRow | null>(null);
    const [bulkImportOpen, setBulkImportOpen] = useState(false);
    const [syncingCatalog, setSyncingCatalog] = useState(false);
    const [exportingMappings, setExportingMappings] = useState(false);

    // Export ALL packaging mappings (not just the filtered/paged view) — blob -> object-URL
    // -> anchor -> click -> revoke, mirroring the other CSV downloads in Carding.
    const handleExportMappings = async () => {
        if (exportingMappings) return;
        setExportingMappings(true);
        try {
            const blob = await WarehouseAPI.exportPackagingMappings();
            const istDate = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(new Date());
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `packaging-mappings-${istDate}.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            toast.success('Exported all packaging mappings', { duration: 4000 });
        } catch (err: any) {
            toast.error(err.response?.data?.message || err.message || 'Failed to export mappings', { duration: 4000 });
        } finally {
            setExportingMappings(false);
        }
    };

    const handleSyncCatalog = async () => {
        setSyncingCatalog(true);
        try {
            const res = await WarehouseAPI.syncPackagingCatalog();
            toast.success(`Packaging catalog synced — ${res.count} packaging SKU${res.count === 1 ? '' : 's'}`, { duration: 4000 });
        } catch (err: any) {
            toast.error(err.response?.data?.message || err.message || 'Failed to sync packaging catalog', { duration: 4000 });
        } finally {
            setSyncingCatalog(false);
        }
    };

    // Filters/sort/page are stored in the URL (prefixed with "c" to avoid colliding with
    // other Carding sub-tabs sharing the same query string) so a refresh — or navigating
    // back — restores exactly where the user left off, instead of resetting every time.
    const [searchParams, setSearchParams] = useSearchParams();
    const updateParam = useCallback((key: string, value: string | null) => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            if (value === null || value === '') next.delete(key);
            else next.set(key, value);
            return next;
        }, { replace: true });
    }, [setSearchParams]);

    // Changing a filter/sort/search resets the page back to 0 in the SAME URL update — React
    // Router's setter doesn't queue back-to-back calls, so clearing "cpage" has to happen
    // inside the one updater that also changes the filter (hence the explicit delete here).
    const updateParamResettingPage = useCallback((key: string, value: string | null) => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            if (value === null || value === '') next.delete(key);
            else next.set(key, value);
            next.delete('cpage');
            return next;
        }, { replace: true });
    }, [setSearchParams]);

    // The search term that actually drives the query + URL. The input below stays controlled
    // on `searchDraft` and is debounced into this so typing doesn't fire a request (or rewrite
    // the URL) on every keystroke — mirrors the History tab.
    const searchQuery = searchParams.get('cq') || '';

    const shelfFilter = searchParams.get('cshelf') || 'all';
    const setShelfFilter = (v: string) => updateParamResettingPage('cshelf', v === 'all' ? null : v);

    const packagingFilter = searchParams.get('cpkg') || 'all';
    const setPackagingFilter = (v: string) => updateParamResettingPage('cpkg', v === 'all' ? null : v);

    const sortKey = (searchParams.get('csort') as SortKey) || 'quantity';
    const setSortKey = (v: SortKey) => updateParamResettingPage('csort', v === 'quantity' ? null : v);

    const sortDir = (searchParams.get('cdir') as 'asc' | 'desc') || 'desc';
    const setSortDir = (v: 'asc' | 'desc' | ((prev: 'asc' | 'desc') => 'asc' | 'desc')) => {
        const resolved = typeof v === 'function' ? v(sortDir) : v;
        updateParamResettingPage('cdir', resolved === 'desc' ? null : resolved);
    };

    const page = parseInt(searchParams.get('cpage') || '0', 10) || 0;
    const setPage = (v: number | ((prev: number) => number)) => {
        const resolved = typeof v === 'function' ? v(page) : v;
        updateParam('cpage', resolved === 0 ? null : String(resolved));
    };

    // Debounced search: the controlled input writes `searchDraft`; after a pause it's pushed
    // into the "cq" URL param (which also resets the page to 0), which in turn drives the
    // server-side query via the react-query key below.
    const [searchDraft, setSearchDraft] = useState(searchQuery);
    useEffect(() => { setSearchDraft(searchQuery); }, [searchQuery]);
    useEffect(() => {
        if (searchDraft === searchQuery) return;
        const t = setTimeout(() => { updateParamResettingPage('cq', searchDraft); }, 300);
        return () => clearTimeout(t);
    }, [searchDraft, searchQuery, updateParamResettingPage]);

    // Server-side paged grid. page/pageSize/search/filters/sort are all in the query key, so
    // each combination is cached and switching back is instant. keepPreviousData keeps the
    // current page rendered while the next loads, so paging/filtering doesn't flash the
    // skeleton. The first page shows fast because the backend returns only one chunk.
    const shelfDataQuery = useQuery<CardingShelfDataPage>({
        queryKey: ['carding-shelf-data', page, DEFAULT_PAGE_SIZE, searchQuery, shelfFilter, packagingFilter, sortKey, sortDir],
        queryFn: () => WarehouseAPI.getCardingShelfData({
            page,
            pageSize: DEFAULT_PAGE_SIZE,
            search: searchQuery,
            shelf: shelfFilter,
            packaging: packagingFilter,
            sort: sortKey,
            dir: sortDir,
        }),
        placeholderData: keepPreviousData,
        staleTime: 60_000,
    });

    // The page of rows currently shown. keepPreviousData means this stays on the last good
    // page during a refetch (and during an error — see the error notice below), so a slow or
    // failed request never blanks the grid.
    const rows = useMemo<CardingShelfRow[]>(
        () => (Array.isArray(shelfDataQuery.data?.data) ? shelfDataQuery.data!.data : []),
        [shelfDataQuery.data]
    );
    const total = shelfDataQuery.data?.total ?? 0;
    // Only the very first load (no page cached yet) shows the skeleton; a background refetch of
    // already-shown data does not.
    const loading = shelfDataQuery.isLoading;
    // A fetch error is kept as a dismissible notice ONLY (see below) instead of replacing the
    // grid — but if we have NO data at all yet (first-ever load failed), fall back to a
    // full-tab error with Retry so the user isn't staring at a blank page.
    const fetchError = shelfDataQuery.error
        ? ((shelfDataQuery.error as any)?.response?.data?.message
            || (shelfDataQuery.error as any)?.message
            || 'Failed to load carding shelf data')
        : null;
    const hasAnyData = !!shelfDataQuery.data;

    // A dismissible "couldn't refresh" banner for when a refetch fails but we still have a
    // previous page to show. Cleared on dismiss and whenever a fetch succeeds again.
    const [refreshNoticeDismissed, setRefreshNoticeDismissed] = useState(false);
    useEffect(() => { if (!shelfDataQuery.error) setRefreshNoticeDismissed(false); }, [shelfDataQuery.error]);
    const showRefreshNotice = !!fetchError && hasAnyData && !refreshNoticeDismissed;

    const [lightboxSections, setLightboxSections] = useState<LightboxSection[] | null>(null);

    const [moveRow, setMoveRow] = useState<CardingShelfRow | null>(null);
    const [toShelf, setToShelf] = useState<string | null>(null);
    const [packagingShelf, setPackagingShelf] = useState<string | null>(null);
    const [packagingShelfTouched, setPackagingShelfTouched] = useState(false);
    const [showAllShelvesOverride, setShowAllShelvesOverride] = useState(false);
    const [moveQty, setMoveQty] = useState<number>(0);
    const [moveSubmitting, setMoveSubmitting] = useState(false);
    // Synchronous submit locks — the *Submitting state only takes effect on the next
    // render, so a same-tick double invocation would initiate (and send) two moves.
    const moveInFlightRef = useRef(false);
    const damageInFlightRef = useRef(false);
    const [moveError, setMoveError] = useState<string | null>(null);
    // Structured packaging-error detail (PACKAGING_NOT_FOUND / PACKAGING_INSUFFICIENT) so the
    // move dialog can show targeted guidance + an "Edit packaging" shortcut, not just a line.
    const [moveErrorInfo, setMoveErrorInfo] = useState<{ code: string; packagingSku?: string; available?: number; needed?: number; shelf?: string } | null>(null);

    const [damageRow, setDamageRow] = useState<CardingShelfRow | null>(null);
    const [damageQty, setDamageQty] = useState<number>(0);
    const [damageSubmitting, setDamageSubmitting] = useState(false);
    const [damageError, setDamageError] = useState<string | null>(null);
    // Which DAMAGE-family shelf (DAMAGE, DAMAGE-01, ...) this move will actually land on —
    // whichever already holds the most of this SKU's damaged stock. Defaults to the plain
    // "DAMAGE" shelf while the real suggestion is still loading.
    const [damageShelfSuggestion, setDamageShelfSuggestion] = useState<string>('DAMAGE');

    const toast = useToast();

    const [targetShelfOptions, setTargetShelfOptions] = useState<string[]>(FALLBACK_SHELF_OPTIONS);
    useEffect(() => {
        WarehouseAPI.getCardingShelfOptions()
            .then(shelves => { if (shelves.length > 0) setTargetShelfOptions(shelves); })
            .catch(() => {/* keep fallback list */ });
    }, []);

    // Background refresh without flipping the page back to a skeleton — react-query keeps
    // the existing cached rows rendered while it refetches. Used by the Plytix sync loop
    // below (previously `fetchShelfData({ silent: true })`).
    const refreshShelfData = useCallback(
        () => queryClient.invalidateQueries({ queryKey: ['carding-shelf-data'] }),
        [queryClient]
    );

    // Optimistically decrement a moved/damaged SKU's quantity on the current cache after a
    // confirmed move. The cache now holds a PAGE object ({ data, total, ... }) keyed by
    // page/filters, and the moved row may sit in any cached page, so we patch EVERY cached
    // shelf-data page (prefix match): decrement the matching row, drop it if it hits zero,
    // and decrement that page's `total` when a row drops so the pager/count stay consistent.
    const applyOptimisticQtyChange = useCallback((skuCode: string, shelfCode: string, delta: number) => {
        queryClient.setQueriesData<CardingShelfDataPage>({ queryKey: ['carding-shelf-data'] }, prev => {
            if (!prev || !Array.isArray(prev.data)) return prev;
            let dropped = 0;
            const nextData = prev.data
                .map(r => (r.skuCode === skuCode && r.shelfCode === shelfCode)
                    ? { ...r, quantity: r.quantity - delta }
                    : r)
                .filter(r => {
                    if (r.quantity > 0) return true;
                    dropped += 1;
                    return false;
                });
            if (dropped === 0 && nextData === prev.data) return prev;
            return { ...prev, data: nextData, total: Math.max(0, prev.total - dropped) };
        });
    }, [queryClient]);

    // Missing Plytix/Redshift data is also synced by a server-side cron job (every 4h for
    // pending SKUs, plus a full unconditional resync of every carding SKU daily at 9am
    // IST). Now that the grid loads one page at a time, this reflects the pending SKUs ON
    // THE CURRENT PAGE — the "Sync now" button still triggers a full server-side resync of
    // every carding SKU regardless, and the cron keeps the whole set synced in the background.
    const pendingSkus = useMemo(() => rows.filter(r => !r.plytixSynced && !r.plytixSyncError), [rows]);

    const handleSyncNow = async () => {
        setSyncingPlytix(true);
        try {
            // This triggers a FULL resync of every carding SKU (not just pending ones), so
            // it also picks up packaging_skus edits made in Redshift for SKUs that already
            // synced before. It runs in the background server-side and can take a minute
            // or two for all carding SKUs, so we don't block on it finishing here — just
            // let the user know it's underway and do one refresh shortly after in case the
            // first few batches have already landed.
            const result = await WarehouseAPI.runCardingSyncNow();
            toast.info(result.message || 'Syncing in the background. This may take a minute.', { duration: 4000 });
            setTimeout(() => { refreshShelfData(); }, 8000);
        } catch (err: any) {
            toast.error(err.response?.data?.message || err.message || 'Failed to start sync', { duration: 4000 });
        } finally {
            setSyncingPlytix(false);
        }
    };

    const [forceSyncingSku, setForceSyncingSku] = useState<string | null>(null);

    // Force a fresh Plytix/Redshift sync for one specific SKU, regardless of whether it
    // was already synced before — needed when the SKU's data (e.g. packaging_sku) was
    // updated in Redshift AFTER our last sync, since the normal pending-only sync loop
    // skips SKUs that already have a successful syncedAt.
    const handleForceResync = async (skuCode: string) => {
        setForceSyncingSku(skuCode);
        try {
            await WarehouseAPI.syncCardingPlytix([skuCode]);
            await refreshShelfData();
            toast.success(`Re-synced ${skuCode} from Plytix/Redshift`, { duration: 4000 });
        } catch (err: any) {
            toast.error(err.response?.data?.message || err.message || `Failed to re-sync ${skuCode}`, { duration: 4000 });
        } finally {
            setForceSyncingSku(null);
        }
    };

    const failedSkus = useMemo(() => rows.filter(r => r.plytixSyncError).map(r => r.skuCode), [rows]);

    const handleRetryFailed = async () => {
        if (failedSkus.length === 0) return;
        setSyncingPlytix(true);
        try {
            // Retry ALL failed SKUs, not just the first batch — with hundreds of failures
            // a single PLYTIX_SYNC_BATCH_SIZE slice used to leave most of them untouched.
            const unique = Array.from(new Set(failedSkus));
            for (let i = 0; i < unique.length; i += PLYTIX_SYNC_BATCH_SIZE) {
                await WarehouseAPI.syncCardingPlytix(unique.slice(i, i + PLYTIX_SYNC_BATCH_SIZE));
            }
            await refreshShelfData();
        } catch {
            // ignore — user can retry again
        } finally {
            setSyncingPlytix(false);
        }
    };

    // Filter dropdown options. Now that the grid is server-paged, a single page no longer
    // contains the full option universe, so we ACCUMULATE every shelf code / packaging SKU
    // seen across the pages loaded so far (plus the live shelf-options list for shelves, and
    // always the currently-selected value) — the dropdowns stay populated as the user pages
    // instead of collapsing to just the current page's values.
    const discoveredShelvesRef = useRef<Set<string>>(new Set());
    const discoveredPackagingRef = useRef<Set<string>>(new Set());
    const [optionsVersion, setOptionsVersion] = useState(0);
    useEffect(() => {
        let changed = false;
        rows.forEach(r => {
            if (r.shelfCode && !discoveredShelvesRef.current.has(r.shelfCode)) { discoveredShelvesRef.current.add(r.shelfCode); changed = true; }
            (r.packagingSkus || []).forEach(p => { if (!discoveredPackagingRef.current.has(p)) { discoveredPackagingRef.current.add(p); changed = true; } });
        });
        if (changed) setOptionsVersion(v => v + 1);
    }, [rows]);

    const shelfOptions = useMemo(() => {
        const set = new Set<string>(discoveredShelvesRef.current);
        // The carding grid shows NONCARDED-family and BLINKIT-NC source shelves, so seed from
        // the live shelf-options list filtered to those — gives the full shelf picker on first load.
        targetShelfOptions.filter(s => /^(NONCARDED|BLINKIT-NC)/i.test(s)).forEach(s => set.add(s));
        if (shelfFilter !== 'all') set.add(shelfFilter);
        return Array.from(set).sort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [optionsVersion, targetShelfOptions, shelfFilter]);

    const packagingOptions = useMemo(() => {
        const set = new Set<string>(discoveredPackagingRef.current);
        if (packagingFilter !== 'all') set.add(packagingFilter);
        return Array.from(set).sort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [optionsVersion, packagingFilter]);

    const handleSort = (key: SortKey) => {
        if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else { setSortKey(key); setSortDir(key === 'quantity' ? 'desc' : 'asc'); }
    };

    // Units summed over the CURRENT PAGE only — the full filtered-set unit total isn't known
    // client-side anymore now that rows arrive one page at a time.
    const pageUnits = useMemo(() => rows.reduce((sum, r) => sum + r.quantity, 0), [rows]);

    const buildSkuSections = (row: CardingShelfRow): LightboxSection[] => [
        { title: row.skuCode, images: row.images || [] },
    ];

    const buildPackagingSections = (packagingSku: string, images: string[]): LightboxSection[] => [
        { title: `Packaging — ${packagingSku}`, images: images || [] },
    ];

    const openMoveDialog = (row: CardingShelfRow) => {
        setMoveRow(row);
        // If this SKU already has stock elsewhere (on a real, non-carding shelf), default
        // straight to that shelf instead of making the user pick from the full list.
        const suggested = row.suggestedShelf || null;
        setToShelf(suggested);
        setPackagingShelf(suggested ? suggestPackagingShelf(suggested) : null);
        setPackagingShelfTouched(false);
        setShowAllShelvesOverride(false);
        setMoveQty(row.quantity);
        setMoveError(null);
        setMoveErrorInfo(null);
    };

    const handleToShelfChange = (val: string | null) => {
        setToShelf(val);
        if (!packagingShelfTouched) {
            setPackagingShelf(val ? suggestPackagingShelf(val) : null);
        }
    };

    const closeMoveDialog = () => {
        if (moveSubmitting) return;
        setMoveRow(null);
    };

    const handleConfirmMove = async () => {
        if (!moveRow || !toShelf) return;
        if (moveQty <= 0 || moveQty > moveRow.quantity) {
            setMoveError(`Quantity must be between 1 and ${moveRow.quantity}`);
            return;
        }
        if (moveInFlightRef.current) return;
        moveInFlightRef.current = true;
        setMoveSubmitting(true);
        setMoveError(null);
        setMoveErrorInfo(null);
        let moveId: string | null = null;
        let rawResponse: any = null;
        try {
            const initiated = await WarehouseAPI.initiateCardingMove({
                skuCode: moveRow.skuCode,
                fromShelf: moveRow.shelfCode,
                toShelf,
                quantity: moveQty,
                packagingShelf,
            });
            moveId = initiated.moveId;

            // Execute the Unicommerce webhook directly from the browser — server-side
            // calls to n8n are IP-blocked (same pattern as CyclicCountPage.tsx).
            const ucResponse = await WarehouseAPI.postInventoryAdjust(
                initiated.n8nPayload,
                `carding-move:${initiated.moveId}`
            );
            rawResponse = ucResponse.data;

            // A missing/empty body, or a response that isn't shaped like a real
            // Unicommerce adjustment result, is treated as a failure — an empty 2xx
            // response is NOT proof the adjustment actually happened.
            const adjustmentResponses = extractAdjustmentResponses(ucResponse.data);
            if (adjustmentResponses.length === 0) {
                throw new Error('Unicommerce returned no adjustment result — the move was NOT confirmed as synced');
            }
            const failedAdjustment = adjustmentResponses.find((r: any) => r.successful === false);
            if (failedAdjustment) {
                const innerError = failedAdjustment.errors?.[0];
                throw new Error(innerError?.description || innerError?.message || 'Unicommerce adjustment failed');
            }

            await WarehouseAPI.confirmCardingMove(moveId, ucResponse.data);

            applyOptimisticQtyChange(moveRow.skuCode, moveRow.shelfCode, moveQty);
            toast.success(`Moved ${moveQty} × ${moveRow.skuCode} from ${moveRow.shelfCode} to ${toShelf}`, { duration: 4000 });
            setMoveRow(null);
        } catch (err: any) {
            const data = err.response?.data;
            const message = data?.message || err.message || 'Failed to execute move';
            setMoveError(message);
            // Keep the structured error so the dialog can render the packaging-mapping cases
            // (PACKAGING_NOT_FOUND / PACKAGING_INSUFFICIENT) with helpful guidance + a shortcut.
            setMoveErrorInfo(data?.errorCode ? {
                code: data.errorCode,
                packagingSku: data.packagingSku,
                available: data.available,
                needed: data.needed,
                shelf: data.shelf,
            } : null);
            if (moveId) {
                WarehouseAPI.failCardingMove(moveId, message, err.response?.data ?? rawResponse).catch(() => {/* non-blocking */ });
            }
        } finally {
            moveInFlightRef.current = false;
            setMoveSubmitting(false);
        }
    };

    const openDamageDialog = (row: CardingShelfRow) => {
        setDamageRow(row);
        setDamageQty(row.quantity);
        setDamageError(null);
        setDamageShelfSuggestion('DAMAGE');
        WarehouseAPI.getDamageShelfSuggestion(row.skuCode)
            .then(res => setDamageShelfSuggestion(res.shelfCode || 'DAMAGE'))
            .catch(() => {/* keep the DAMAGE default — backend still resolves the real target on submit */ });
    };

    const closeDamageDialog = () => {
        if (damageSubmitting) return;
        setDamageRow(null);
    };

    const handleConfirmDamage = async () => {
        if (!damageRow) return;
        if (damageQty <= 0 || damageQty > damageRow.quantity) {
            setDamageError(`Quantity must be between 1 and ${damageRow.quantity}`);
            return;
        }
        if (damageInFlightRef.current) return;
        damageInFlightRef.current = true;
        setDamageSubmitting(true);
        setDamageError(null);
        let moveId: string | null = null;
        let rawResponse: any = null;
        try {
            const initiated = await WarehouseAPI.initiateDamageMove({
                skuCode: damageRow.skuCode,
                fromShelf: damageRow.shelfCode,
                quantity: damageQty,
            });
            moveId = initiated.moveId;

            const ucResponse = await WarehouseAPI.postInventoryAdjust(
                initiated.n8nPayload,
                `carding-move:${initiated.moveId}`
            );
            rawResponse = ucResponse.data;

            const adjustmentResponses = extractAdjustmentResponses(ucResponse.data);
            if (adjustmentResponses.length === 0) {
                throw new Error('Unicommerce returned no adjustment result — the damage move was NOT confirmed as synced');
            }
            const failedAdjustment = adjustmentResponses.find((r: any) => r.successful === false);
            if (failedAdjustment) {
                const innerError = failedAdjustment.errors?.[0];
                throw new Error(innerError?.description || innerError?.message || 'Unicommerce adjustment failed');
            }

            await WarehouseAPI.confirmCardingMove(moveId, ucResponse.data);

            applyOptimisticQtyChange(damageRow.skuCode, damageRow.shelfCode, damageQty);
            toast.success(`Marked ${damageQty} × ${damageRow.skuCode} as damaged from ${damageRow.shelfCode} → ${initiated.toShelf}`, { duration: 4000 });
            setDamageRow(null);
        } catch (err: any) {
            const message = err.response?.data?.message || err.message || 'Failed to mark as damaged';
            setDamageError(message);
            if (moveId) {
                WarehouseAPI.failCardingMove(moveId, message, err.response?.data ?? rawResponse).catch(() => {/* non-blocking */ });
            }
        } finally {
            damageInFlightRef.current = false;
            setDamageSubmitting(false);
        }
    };

    const packagingDeductionPreview = moveRow && moveQty > 0
        ? (moveRow.packagingSkus || []).map(pSku => ({ sku: pSku, qty: moveQty }))
        : [];

    if (loading) {
        return (
            <Box sx={{ p: 2 }}>
                {[1, 2, 3, 4, 5].map(i => (
                    <Skeleton key={i} variant="rounded" height={56} sx={{ mb: 1, borderRadius: 2 }} />
                ))}
            </Box>
        );
    }

    // Full-tab error ONLY when the very first load failed and we have NO page to show. Once
    // any page has loaded, a later failure is handled by the non-blocking refresh notice
    // below (the previous page stays on screen), never a full-screen wipe.
    if (fetchError && !hasAnyData) {
        return <Alert severity="error" action={<Button onClick={() => shelfDataQuery.refetch()}>Retry</Button>} sx={{ m: 2 }}>{fetchError}</Alert>;
    }

    return (
        <Box>
            {/* Toolbar — two clear zones: LEFT = find/scope (search, filters, live count);
                RIGHT = sync & import actions. space-between keeps them visually separated and
                each zone wraps internally on small screens instead of one messy band. */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
                {/* LEFT: find / scope */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <TextField
                        size="small"
                        placeholder="Search by SKU or shelf…"
                        value={searchDraft}
                        onChange={(e) => setSearchDraft(e.target.value)}
                        sx={{ minWidth: 240, '& .MuiOutlinedInput-root': { borderRadius: '999px', bgcolor: '#fff' } }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search sx={{ fontSize: 18, color: '#8A8078' }} />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <FilterDropdown
                        label="Shelf"
                        value={shelfFilter}
                        onChange={setShelfFilter}
                        allLabel="All shelves"
                        minWidth={170}
                        options={shelfOptions.map(s => ({ value: s, label: s }))}
                    />

                    <FilterDropdown
                        label="Packaging SKU"
                        value={packagingFilter}
                        onChange={setPackagingFilter}
                        allLabel="All packaging"
                        minWidth={190}
                        options={packagingOptions.map(s => ({ value: s, label: s }))}
                    />

                    {/* Live count — quiet caption, not a competing control. `total` is the full
                        matching-row count from the server; units shown are for the current page
                        (the full-set unit total isn't loaded client-side anymore). */}
                    <Typography variant="body2" sx={{ color: '#8A8078', fontWeight: 600, whiteSpace: 'nowrap', ml: 0.5 }}>
                        {total.toLocaleString()} SKUs · {pageUnits.toLocaleString()} units on this page
                    </Typography>
                </Box>

                {/* RIGHT: sync & import actions — one consistent outlined style, grouped together */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    {syncingPlytix ? (
                        <Chip
                            icon={<CircularProgress size={12} sx={{ color: '#b8451e !important' }} />}
                            label="Syncing images…"
                            size="small"
                            variant="outlined"
                            sx={{ fontWeight: 600, borderColor: '#F0D9CC', color: '#b8451e', bgcolor: '#FBF3EE' }}
                        />
                    ) : (
                        <Chip
                            icon={<Refresh sx={{ fontSize: 14 }} />}
                            label={pendingSkus.length > 0 ? `Sync ${pendingSkus.length} pending` : 'Sync packaging & images'}
                            size="small"
                            variant="outlined"
                            onClick={handleSyncNow}
                            sx={{ fontWeight: 600, borderColor: '#F0D9CC', color: '#b8451e', bgcolor: '#fff', cursor: 'pointer', '&:hover': { bgcolor: '#FBF3EE' } }}
                        />
                    )}
                    {!syncingPlytix && failedSkus.length > 0 && (
                        <Chip
                            icon={<Refresh sx={{ fontSize: 14 }} />}
                            label={`Retry ${failedSkus.length} failed`}
                            size="small"
                            variant="outlined"
                            onClick={handleRetryFailed}
                            sx={{ fontWeight: 600, borderColor: '#FECACA', color: '#b91c1c', bgcolor: '#fff', cursor: 'pointer', '&:hover': { bgcolor: '#fef2f2' } }}
                        />
                    )}

                    {/* subtle divider between the always-available sync and the packaging-mapping actions */}
                    <Box sx={{ width: '1px', height: 22, bgcolor: '#ECECE4', mx: 0.25 }} />

                    {canEdit && (
                        <Chip
                            icon={<PlaylistAddCheck sx={{ fontSize: 15 }} />}
                            label="Import mappings"
                            size="small"
                            variant="outlined"
                            onClick={() => setBulkImportOpen(true)}
                            sx={{ fontWeight: 600, borderColor: '#DAD6CC', color: '#4A433D', bgcolor: '#fff', cursor: 'pointer', '&:hover': { bgcolor: '#FAFAF7' } }}
                        />
                    )}

                    <Chip
                        icon={exportingMappings
                            ? <CircularProgress size={12} sx={{ color: '#4A433D !important' }} />
                            : <Download sx={{ fontSize: 15 }} />}
                        label="Export mappings"
                        size="small"
                        variant="outlined"
                        onClick={exportingMappings ? undefined : handleExportMappings}
                        sx={{ fontWeight: 600, borderColor: '#DAD6CC', color: '#4A433D', bgcolor: '#fff', cursor: exportingMappings ? 'default' : 'pointer', '&:hover': { bgcolor: '#FAFAF7' } }}
                    />

                    {canSyncCatalog && (
                        <Chip
                            icon={syncingCatalog
                                ? <CircularProgress size={12} sx={{ color: '#4A433D !important' }} />
                                : <CloudSync sx={{ fontSize: 15 }} />}
                            label="Sync catalog"
                            size="small"
                            variant="outlined"
                            onClick={syncingCatalog ? undefined : handleSyncCatalog}
                            sx={{ fontWeight: 600, borderColor: '#DAD6CC', color: '#4A433D', bgcolor: '#fff', cursor: syncingCatalog ? 'default' : 'pointer', '&:hover': { bgcolor: '#FAFAF7' } }}
                        />
                    )}
                </Box>
            </Box>

            {/* Non-blocking refresh failure: a slow/failed/timed-out refetch keeps the last
                good page on screen (keepPreviousData) and surfaces here as a small dismissible
                notice with Retry — it NEVER wipes the grid to a full-screen error. */}
            {showRefreshNotice && (
                <Alert
                    severity="warning"
                    onClose={() => setRefreshNoticeDismissed(true)}
                    action={
                        <Button color="inherit" size="small" onClick={() => shelfDataQuery.refetch()}>
                            Retry
                        </Button>
                    }
                    sx={{ mb: 1.5 }}
                >
                    Couldn't refresh — showing the last loaded data. {fetchError}
                </Alert>
            )}

            {rows.length === 0 ? (
                <Alert severity="info">No SKUs match the current filters.</Alert>
            ) : (
                <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{ borderRadius: '14px', border: '1px solid #ECECE4', bgcolor: '#fff', overflow: 'hidden' }}
                >
                    <Table size="small" stickyHeader sx={{ minWidth: 900 }}>
                        <TableHead>
                            <TableRow sx={{ '& .MuiTableCell-head': { bgcolor: '#FAFAF7', fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.05, color: '#4A433D', py: 1.5 } }}>
                                <TableCell>
                                    <TableSortLabel active={sortKey === 'skuCode'} direction={sortDir} onClick={() => handleSort('skuCode')}>
                                        SKU
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel active={sortKey === 'shelfCode'} direction={sortDir} onClick={() => handleSort('shelfCode')}>
                                        Shelf
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="right">
                                    <TableSortLabel active={sortKey === 'quantity'} direction={sortDir} onClick={() => handleSort('quantity')}>
                                        Qty
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>Packaging SKU</TableCell>
                                <TableCell sx={{ width: 130 }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <AnimatePresence initial={false}>
                                {rows.map((row, idx) => (
                                    <MotionTableRow
                                        key={`${row.skuCode}::${row.shelfCode}`}
                                        hover
                                        initial={{ opacity: 0, y: 8 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, height: 0 }}
                                        transition={{ duration: 0.22, delay: Math.min(idx, 12) * 0.02, ease: 'easeOut' }}
                                    >
                                        {/* Merged Image + SKU cell — thumbnail paired with the SKU code
                                            (same thumbnail+label pattern as the packaging cells). */}
                                        <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <ImageThumb
                                                    images={row.images}
                                                    label={row.skuCode}
                                                    onClick={() => setLightboxSections(buildSkuSections(row))}
                                                />
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                                                    <span>{row.skuCode}</span>
                                                    <Tooltip title="Force re-sync images/packaging from Plytix/Redshift">
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleForceResync(row.skuCode)}
                                                                disabled={forceSyncingSku === row.skuCode}
                                                                sx={{ p: 0.25 }}
                                                            >
                                                                {forceSyncingSku === row.skuCode
                                                                    ? <CircularProgress size={12} />
                                                                    : <Refresh sx={{ fontSize: 14, color: '#000000' }} />}
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={row.shelfCode} size="small" variant="outlined" sx={{ fontFamily: 'monospace', fontWeight: 600 }} />
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums' }}>
                                            {row.quantity.toLocaleString()}
                                        </TableCell>
                                        <TableCell>
                                            {(row.packagingSkus || []).length === 0 ? (
                                                <Typography variant="caption" color="text.disabled">
                                                    {row.plytixSyncError ? 'sync failed' : row.plytixSynced ? 'none linked' : 'syncing…'}
                                                </Typography>
                                            ) : (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                    {(row.packagingSkus || []).map(pSku => {
                                                        const pSkuUpper = pSku.toUpperCase();
                                                        // Images are looked up from the { sku, images }[] payload by sku.
                                                        // Backward-compatible: if packagingImages is absent/old-shaped,
                                                        // find() yields undefined -> no image (the chip still renders).
                                                        const pImages = (row.packagingImages || []).find(pi => pi.sku?.toUpperCase() === pSkuUpper)?.images || [];
                                                        const invArr = row.packagingInventory?.[pSkuUpper] || [];
                                                        const totalInv = invArr.reduce((sum, item) => sum + item.quantity, 0);

                                                        const tooltipContent = invArr.length > 0 ? (
                                                            <Box>
                                                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, borderBottom: '1px solid rgba(255,255,255,0.2)', pb: 0.5 }}>
                                                                    Total: {totalInv.toLocaleString()}
                                                                </Typography>
                                                                {invArr.map(inv => (
                                                                    <Typography key={inv.shelfCode} variant="caption" sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                                                                        <span>{inv.shelfCode}</span>
                                                                        <span>{inv.quantity.toLocaleString()}</span>
                                                                    </Typography>
                                                                ))}
                                                            </Box>
                                                        ) : "No Inventory";

                                                        return (
                                                            <Box key={pSku} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                <ImageThumb
                                                                    images={pImages}
                                                                    label={pSku}
                                                                    size={28}
                                                                    onClick={() => setLightboxSections(buildPackagingSections(pSku, pImages))}
                                                                />
                                                                <Tooltip title={tooltipContent} arrow placement="top">
                                                                    <Chip 
                                                                        label={`${pSku} (${totalInv.toLocaleString()})`} 
                                                                        size="small" 
                                                                        sx={{ fontFamily: 'monospace', fontSize: '0.65rem', bgcolor: '#fef3c7', cursor: 'help' }} 
                                                                    />
                                                                </Tooltip>
                                                            </Box>
                                                        );
                                                    })}
                                                </Box>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {canEdit ? (
                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        startIcon={<LocalShipping sx={{ fontSize: 16 }} />}
                                                        onClick={() => openMoveDialog(row)}
                                                        sx={{ textTransform: 'none', fontWeight: 600 }}
                                                    >
                                                        Move
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        color="error"
                                                        startIcon={<WarningAmber sx={{ fontSize: 16 }} />}
                                                        onClick={() => openDamageDialog(row)}
                                                        sx={{ textTransform: 'none', fontWeight: 600 }}
                                                    >
                                                        Damage
                                                    </Button>
                                                    <Tooltip title="Edit packaging SKUs">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => setEditPkgRow(row)}
                                                            sx={{ border: '1px solid #ECECE4', borderRadius: '8px', color: '#b8451e' }}
                                                        >
                                                            <Edit sx={{ fontSize: 16 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            ) : (
                                                <Typography variant="caption" color="text.disabled">View only</Typography>
                                            )}
                                        </TableCell>
                                    </MotionTableRow>
                                ))}
                            </AnimatePresence>
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <TablePaginationBar page={page} total={total} rowsPerPage={DEFAULT_PAGE_SIZE} onPageChange={setPage} />

            <ImageLightbox
                open={!!lightboxSections}
                onClose={() => setLightboxSections(null)}
                sections={lightboxSections || []}
            />

            <Dialog
                open={!!moveRow}
                onClose={closeMoveDialog}
                maxWidth="xs"
                fullWidth
                slotProps={{ transition: { timeout: 260 } }}
                PaperProps={{
                    sx: {
                        borderRadius: '20px',
                        overflow: 'hidden',
                        boxShadow: '0 24px 60px -12px rgba(220, 88, 41,0.25), 0 8px 24px -8px rgba(26, 20, 15,0.15)',
                    },
                }}
            >
                {moveRow && (
                    <motion.div
                        initial={{ opacity: 0, y: 14, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ type: 'spring', stiffness: 340, damping: 28 }}
                    >
                        {/* Header */}
                        <Box sx={{
                            px: 3, pt: 2.5, pb: 2,
                            background: 'linear-gradient(135deg, rgba(220, 88, 41, 0.06) 0%, #ffffff 65%)',
                            borderBottom: '1px solid rgba(220, 88, 41, 0.08)',
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Box sx={{
                                    width: 38, height: 38, borderRadius: '12px', flexShrink: 0,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    background: 'linear-gradient(135deg, #E37E5A 0%, #B8451E 100%)',
                                    boxShadow: '0 4px 14px rgba(220, 88, 41,0.35)',
                                }}>
                                    <LocalShipping sx={{ fontSize: 19, color: '#fff' }} />
                                </Box>
                                <Box>
                                    <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', letterSpacing: '-0.01em', lineHeight: 1.25 }}>
                                        Move to Carded Shelf
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#9498b3', fontWeight: 500 }}>
                                        Transfer inventory &amp; linked packaging
                                    </Typography>
                                </Box>
                            </Box>
                            <IconButton
                                size="small"
                                onClick={closeMoveDialog}
                                disabled={moveSubmitting}
                                sx={{ color: '#000000', '&:hover': { bgcolor: 'rgba(220, 88, 41, 0.08)', color: '#b8451e' } }}
                            >
                                <Close sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Box>

                        <DialogContent sx={{ p: 3 }}>
                            {/* SKU summary card */}
                            <Box sx={{
                                display: 'flex', alignItems: 'center', gap: 1.75, mb: 3,
                                p: 1.75, borderRadius: '14px', bgcolor: '#fafafa', border: '1px solid #f1f1f4',
                            }}>
                                <Box sx={{ borderRadius: '10px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(26, 20, 15,0.08)', flexShrink: 0 }}>
                                    <ImageThumb
                                        images={moveRow.images}
                                        label={moveRow.skuCode}
                                        size={48}
                                        onClick={() => setLightboxSections(buildSkuSections(moveRow))}
                                    />
                                </Box>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography noWrap sx={{ fontFamily: '"JetBrains Mono", monospace', fontWeight: 700, fontSize: '0.92rem', letterSpacing: '-0.01em' }}>
                                        {moveRow.skuCode}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#000000', fontWeight: 500 }}>
                                        <Box component="span" sx={{ color: '#1e293b', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                                            {moveRow.quantity.toLocaleString()}
                                        </Box>{' '}available on {moveRow.shelfCode}
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Destination */}
                            <Typography variant="overline" sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#a3a3c2', letterSpacing: '0.06em', display: 'block', mb: 1 }}>
                                Destination
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1 }}>
                                <Chip
                                    label={moveRow.shelfCode}
                                    size="small"
                                    sx={{ fontFamily: '"JetBrains Mono", monospace', fontWeight: 700, fontSize: '0.72rem', bgcolor: '#FAFAF7', color: '#4A433D' }}
                                />
                                <motion.div
                                    animate={{ x: [0, 3, 0] }}
                                    transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
                                    style={{ display: 'flex' }}
                                >
                                    <ArrowForward sx={{ fontSize: 16, color: '#c4c4de' }} />
                                </motion.div>
                                <Autocomplete
                                    freeSolo
                                    size="small"
                                    options={
                                        moveRow.suggestedShelf && !showAllShelvesOverride
                                            ? [moveRow.suggestedShelf]
                                            : targetShelfOptions.filter(s => s !== moveRow.shelfCode)
                                    }
                                    value={toShelf}
                                    onChange={(_, val) => handleToShelfChange(val)}
                                    onInputChange={(_, val) => handleToShelfChange(val)}
                                    sx={{
                                        flex: 1,
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '10px',
                                            fontFamily: '"JetBrains Mono", monospace',
                                            fontSize: '0.85rem',
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#DC5829', borderWidth: '1.5px' },
                                        },
                                    }}
                                    renderInput={(params) => (
                                        <TextField {...params} placeholder="e.g. CARDING" />
                                    )}
                                />
                            </Box>

                            <AnimatePresence initial={false}>
                                {moveRow.suggestedShelf && !showAllShelvesOverride && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -4, height: 0 }}
                                        animate={{ opacity: 1, y: 0, height: 'auto' }}
                                        exit={{ opacity: 0, height: 0 }}
                                        transition={{ duration: 0.18 }}
                                        style={{ overflow: 'hidden' }}
                                    >
                                        <Box
                                            onClick={() => setShowAllShelvesOverride(true)}
                                            sx={{
                                                display: 'inline-flex', alignItems: 'center', gap: 0.6, mt: 1, mb: 0.5,
                                                px: 1.25, py: 0.5, borderRadius: '999px', bgcolor: 'rgba(220, 88, 41, 0.06)', cursor: 'pointer',
                                                transition: 'background-color 150ms ease',
                                                '&:hover': { bgcolor: 'rgba(220, 88, 41, 0.08)' },
                                            }}
                                        >
                                            <AutoAwesome sx={{ fontSize: 13, color: '#b8451e' }} />
                                            <Typography sx={{ color: '#b8451e', fontWeight: 600, fontSize: '0.72rem' }}>
                                                Already on {moveRow.suggestedShelf} — show all shelves
                                            </Typography>
                                        </Box>
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            <Box sx={{ mb: 3 }} />

                            {/* Quantity */}
                            <Typography variant="overline" sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#a3a3c2', letterSpacing: '0.06em', display: 'block', mb: 1 }}>
                                Quantity
                            </Typography>
                            <TextField
                                fullWidth
                                type="number"
                                size="small"
                                value={moveQty}
                                onChange={(e) => setMoveQty(Math.max(0, Math.min(moveRow.quantity, parseInt(e.target.value, 10) || 0)))}
                                inputProps={{ min: 1, max: moveRow.quantity, style: { fontVariantNumeric: 'tabular-nums', fontWeight: 700, fontSize: '0.95rem' } }}
                                helperText={`Max ${moveRow.quantity.toLocaleString()} units`}
                                sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                            />

                            <AnimatePresence initial={false}>
                                {packagingDeductionPreview.length > 0 && (
                                    <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        exit={{ opacity: 0, height: 0 }}
                                        transition={{ duration: 0.22, ease: 'easeOut' }}
                                        style={{ overflow: 'hidden' }}
                                    >
                                        <Box sx={{
                                            mb: 3, p: 2, borderRadius: '14px', border: '1px solid #fde68a',
                                            background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.25 }}>
                                                <Inventory2 sx={{ fontSize: 15, color: '#b45309' }} />
                                                <Typography sx={{ fontWeight: 700, color: '#92400e', fontSize: '0.7rem', letterSpacing: '0.04em' }}>
                                                    PACKAGING WILL ALSO TRANSFER
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 1.75 }}>
                                                {packagingDeductionPreview.map((p, i) => (
                                                    <motion.div
                                                        key={p.sku}
                                                        initial={{ opacity: 0, x: -8 }}
                                                        animate={{ opacity: 1, x: 0 }}
                                                        transition={{ delay: i * 0.05, duration: 0.2, ease: 'easeOut' }}
                                                    >
                                                        <Box sx={{
                                                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                                            px: 1.25, py: 0.85, borderRadius: '8px', bgcolor: 'rgba(255,255,255,0.65)',
                                                        }}>
                                                            <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.78rem', fontWeight: 600, color: '#78350f' }}>
                                                                {p.sku}
                                                            </Typography>
                                                            <Typography sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', fontSize: '0.78rem', color: '#b45309' }}>
                                                                −{p.qty}
                                                            </Typography>
                                                        </Box>
                                                    </motion.div>
                                                ))}
                                            </Box>
                                            <Autocomplete
                                                freeSolo
                                                size="small"
                                                options={targetShelfOptions}
                                                value={packagingShelf}
                                                onChange={(_, val) => { setPackagingShelf(val); setPackagingShelfTouched(true); }}
                                                onInputChange={(_, val) => { setPackagingShelf(val); setPackagingShelfTouched(true); }}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: '10px', bgcolor: '#fff',
                                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#d97706', borderWidth: '1.5px' },
                                                    },
                                                }}
                                                renderInput={(params) => (
                                                    <TextField {...params} label="Packaging destination shelf" placeholder="e.g. D2C-CONSUMPTION" />
                                                )}
                                            />
                                        </Box>
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            <AnimatePresence initial={false}>
                                {moveError && (
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1, x: [0, -6, 6, -4, 4, 0] }}
                                        exit={{ opacity: 0 }}
                                        transition={{ duration: 0.4 }}
                                    >
                                        {moveErrorInfo?.code === 'PACKAGING_NOT_FOUND' ? (
                                            <Alert
                                                severity="warning"
                                                icon={<WarningAmber />}
                                                sx={{ mb: 2.5, borderRadius: '10px', '& .MuiAlert-message': { width: '100%' } }}
                                                action={moveRow && (
                                                    <Button
                                                        size="small"
                                                        startIcon={<Edit sx={{ fontSize: 15 }} />}
                                                        onClick={() => { setEditPkgRow(moveRow); }}
                                                        sx={{ textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}
                                                    >
                                                        Edit packaging
                                                    </Button>
                                                )}
                                            >
                                                <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', mb: 0.25 }}>
                                                    Packaging not found in inventory
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.8rem' }}>
                                                    <Chip
                                                        label={moveErrorInfo.packagingSku}
                                                        size="small"
                                                        sx={{ fontFamily: 'monospace', fontWeight: 700, height: 20, mr: 0.5, bgcolor: '#fef3c7', color: '#92400e' }}
                                                    />
                                                    is mapped to this SKU but has no stock anywhere in Unicommerce. It may be mistyped or discontinued in the packaging mapping. Fix the mapping, then try the move again.
                                                </Typography>
                                            </Alert>
                                        ) : moveErrorInfo?.code === 'PACKAGING_INSUFFICIENT' ? (
                                            <Alert severity="warning" icon={<WarningAmber />} sx={{ mb: 2.5, borderRadius: '10px' }}>
                                                <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', mb: 0.25 }}>Not enough packaging stock</Typography>
                                                <Typography sx={{ fontSize: '0.8rem' }}>
                                                    <Chip label={moveErrorInfo.packagingSku} size="small" sx={{ fontFamily: 'monospace', fontWeight: 700, height: 20, mr: 0.5, bgcolor: '#fef3c7', color: '#92400e' }} />
                                                    has only <strong>{moveErrorInfo.available}</strong> unit{moveErrorInfo.available === 1 ? '' : 's'} on {moveErrorInfo.shelf}, but this move needs <strong>{moveErrorInfo.needed}</strong>.
                                                </Typography>
                                            </Alert>
                                        ) : (
                                            <Alert severity="error" sx={{ mb: 2.5, borderRadius: '10px' }}>{moveError}</Alert>
                                        )}
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            <Box sx={{ display: 'flex', gap: 1.25, justifyContent: 'flex-end', pt: 0.5 }}>
                                <Button
                                    onClick={closeMoveDialog}
                                    disabled={moveSubmitting}
                                    sx={{ textTransform: 'none', fontWeight: 600, color: '#6B625B', borderRadius: '10px', px: 2 }}
                                >
                                    Cancel
                                </Button>
                                <motion.div whileTap={{ scale: 0.96 }} style={{ display: 'inline-block' }}>
                                    <Button
                                        variant="contained"
                                        onClick={handleConfirmMove}
                                        disabled={moveSubmitting || !toShelf || moveQty <= 0 || (packagingDeductionPreview.length > 0 && !packagingShelf)}
                                        sx={{
                                            textTransform: 'none', fontWeight: 700, borderRadius: '10px', px: 2.5, py: 1,
                                            background: 'linear-gradient(135deg, #E37E5A 0%, #B8451E 100%)',
                                            boxShadow: '0 4px 14px rgba(220, 88, 41,0.35)',
                                            '&:hover': { boxShadow: '0 6px 18px rgba(220, 88, 41,0.45)' },
                                            '&.Mui-disabled': { background: '#e5e7eb', boxShadow: 'none' },
                                        }}
                                    >
                                        {moveSubmitting ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Confirm Move'}
                                    </Button>
                                </motion.div>
                            </Box>
                        </DialogContent>
                    </motion.div>
                )}
            </Dialog>

            <Dialog
                open={!!damageRow}
                onClose={closeDamageDialog}
                maxWidth="xs"
                fullWidth
                slotProps={{ transition: { timeout: 260 } }}
                PaperProps={{
                    sx: {
                        borderRadius: '20px',
                        overflow: 'hidden',
                        boxShadow: '0 24px 60px -12px rgba(220,38,38,0.22), 0 8px 24px -8px rgba(26, 20, 15,0.15)',
                    },
                }}
            >
                {damageRow && (
                    <motion.div
                        initial={{ opacity: 0, y: 14, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ type: 'spring', stiffness: 340, damping: 28 }}
                    >
                        {/* Header */}
                        <Box sx={{
                            px: 3, pt: 2.5, pb: 2,
                            background: 'linear-gradient(135deg, #fef2f2 0%, #ffffff 65%)',
                            borderBottom: '1px solid #fecaca',
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Box sx={{
                                    width: 38, height: 38, borderRadius: '12px', flexShrink: 0,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    background: 'linear-gradient(135deg, #f87171 0%, #dc2626 100%)',
                                    boxShadow: '0 4px 14px rgba(220,38,38,0.35)',
                                }}>
                                    <WarningAmber sx={{ fontSize: 19, color: '#fff' }} />
                                </Box>
                                <Box>
                                    <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', letterSpacing: '-0.01em', lineHeight: 1.25, color: '#7f1d1d' }}>
                                        Mark as Damaged
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#c4919b', fontWeight: 500 }}>
                                        No packaging is affected by this action
                                    </Typography>
                                </Box>
                            </Box>
                            <IconButton
                                size="small"
                                onClick={closeDamageDialog}
                                disabled={damageSubmitting}
                                sx={{ color: '#f0a8a8', '&:hover': { bgcolor: '#fee2e2', color: '#dc2626' } }}
                            >
                                <Close sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Box>

                        <DialogContent sx={{ p: 3 }}>
                            <Box sx={{
                                display: 'flex', alignItems: 'center', gap: 1.75, mb: 3,
                                p: 1.75, borderRadius: '14px', bgcolor: '#fafafa', border: '1px solid #f1f1f4',
                            }}>
                                <Box sx={{ borderRadius: '10px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(26, 20, 15,0.08)', flexShrink: 0 }}>
                                    <ImageThumb images={damageRow.images} label={damageRow.skuCode} size={48} onClick={() => setLightboxSections(buildSkuSections(damageRow))} />
                                </Box>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography noWrap sx={{ fontFamily: '"JetBrains Mono", monospace', fontWeight: 700, fontSize: '0.92rem', letterSpacing: '-0.01em' }}>
                                        {damageRow.skuCode}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#000000', fontWeight: 500 }}>
                                        <Box component="span" sx={{ color: '#1e293b', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                                            {damageRow.quantity.toLocaleString()}
                                        </Box>{' '}available on {damageRow.shelfCode}
                                    </Typography>
                                </Box>
                            </Box>

                            <Typography variant="overline" sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#a3a3c2', letterSpacing: '0.06em', display: 'block', mb: 1 }}>
                                Destination
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 3 }}>
                                <Chip
                                    label={damageRow.shelfCode}
                                    size="small"
                                    sx={{ fontFamily: '"JetBrains Mono", monospace', fontWeight: 700, fontSize: '0.72rem', bgcolor: '#FAFAF7', color: '#4A433D' }}
                                />
                                <motion.div
                                    animate={{ x: [0, 3, 0] }}
                                    transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
                                    style={{ display: 'flex' }}
                                >
                                    <ArrowForward sx={{ fontSize: 16, color: '#f0a8a8' }} />
                                </motion.div>
                                <Chip
                                    label={damageShelfSuggestion}
                                    size="small"
                                    sx={{ fontFamily: '"JetBrains Mono", monospace', fontWeight: 700, fontSize: '0.72rem', bgcolor: '#fee2e2', color: '#b91c1c' }}
                                />
                            </Box>

                            <Typography variant="overline" sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#a3a3c2', letterSpacing: '0.06em', display: 'block', mb: 1 }}>
                                Quantity
                            </Typography>
                            <TextField
                                fullWidth
                                type="number"
                                size="small"
                                value={damageQty}
                                onChange={(e) => setDamageQty(Math.max(0, Math.min(damageRow.quantity, parseInt(e.target.value, 10) || 0)))}
                                inputProps={{ min: 1, max: damageRow.quantity, style: { fontVariantNumeric: 'tabular-nums', fontWeight: 700, fontSize: '0.95rem' } }}
                                helperText={`Max ${damageRow.quantity.toLocaleString()} units — no packaging is affected by this action`}
                                sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                            />

                            <AnimatePresence initial={false}>
                                {damageError && (
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1, x: [0, -6, 6, -4, 4, 0] }}
                                        exit={{ opacity: 0 }}
                                        transition={{ duration: 0.4 }}
                                    >
                                        <Alert severity="error" sx={{ mb: 2.5, borderRadius: '10px' }}>{damageError}</Alert>
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            <Box sx={{ display: 'flex', gap: 1.25, justifyContent: 'flex-end', pt: 0.5 }}>
                                <Button
                                    onClick={closeDamageDialog}
                                    disabled={damageSubmitting}
                                    sx={{ textTransform: 'none', fontWeight: 600, color: '#6B625B', borderRadius: '10px', px: 2 }}
                                >
                                    Cancel
                                </Button>
                                <motion.div whileTap={{ scale: 0.96 }} style={{ display: 'inline-block' }}>
                                    <Button
                                        variant="contained"
                                        onClick={handleConfirmDamage}
                                        disabled={damageSubmitting || damageQty <= 0}
                                        sx={{
                                            textTransform: 'none', fontWeight: 700, borderRadius: '10px', px: 2.5, py: 1,
                                            background: 'linear-gradient(135deg, #f87171 0%, #dc2626 100%)',
                                            boxShadow: '0 4px 14px rgba(220,38,38,0.35)',
                                            '&:hover': { boxShadow: '0 6px 18px rgba(220,38,38,0.45)' },
                                            '&.Mui-disabled': { background: '#e5e7eb', boxShadow: 'none' },
                                        }}
                                    >
                                        {damageSubmitting ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Confirm Damage'}
                                    </Button>
                                </motion.div>
                            </Box>
                        </DialogContent>
                    </motion.div>
                )}
            </Dialog>

            <EditPackagingDialog
                open={!!editPkgRow}
                skuCode={editPkgRow?.skuCode || ''}
                onClose={() => setEditPkgRow(null)}
                onSaved={() => {
                    setEditPkgRow(null);
                    refreshShelfData();
                    toast.success('Packaging updated', { duration: 4000 });
                }}
            />

            <BulkPackagingImportDialog
                open={bulkImportOpen}
                onClose={() => setBulkImportOpen(false)}
                onApplied={() => {
                    refreshShelfData();
                    toast.success('Packaging mappings imported', { duration: 4000 });
                }}
            />

        </Box>
    );
};

export default CardingTab;
